import React, { useState } from 'react';
import { FiMoreHorizontal } from 'react-icons/fi';
import { EmptyState } from '@/components/custom/empty-state';
import Modal from '@/app/components/Modal';
import ParticipantsForm from './ParticipantsForm';
import Dropdown from '@/app/components/Dropdown';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useEffect } from 'react';
import Swal from 'sweetalert2';
import usePermission from '@/hooks/usePermission';
import { UserInfo } from '@/components/custom/user';
import { Loader } from '@/components/custom/loader';
import moment from 'moment';
import { t } from 'i18next';
import { DashboardContent } from '@/layouts/dashboard';

export function ManageClassroomParticipants(props) {
  const { classroomId, uri } = props;
  const client = useClient();
  const [showModal, setShowModal] = useState(false);
  const [classroom, setClassroom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [classroomManagers, setClassroomManagers] = useState([]);
  const [classroomStudents, setClassroomStudents] = useState([]);

  const getClassroom = async () => {
    const { data, error } = await client.fetch(`classrooms/${classroomId}`);
    if (error) {
      toast.error(t('toast.errorGetClassesInfo'));
    } else {
      setClassroom(data);
    }
  };

  const getAllUsers = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`classrooms/${classroomId}/users`);
    if (error) {
      toast.error(t('toast.errorGetParticipants'));
    } else {
      setAllUsers(data);
      setLoading(false);
    }
  };

  const addParticipantToClassroom = async id => {
    const updatedUserIds = [...classroom?.['user-ids'], id];
    const { data, error } = await client.mutate(['classrooms', classroom.id], {
      'user-ids': updatedUserIds
    });
    if (error) {
      toast.error(t('toast.errorAddParticipant'));
    } else {
      const updatedClassroom = { ...data, 'user-ids': updatedUserIds };
      setClassroom(updatedClassroom);
      getAllUsers();
      toast.success(t('toast.successAddParticipant'));
    }
  };

  const deleteParticipantFromClassroom = async id => {
    const updatedUserIds = classroom['user-ids'].filter(i => i !== id);
    const { data, error } = await client.mutate(['classrooms', classroom.id], {
      'user-ids': updatedUserIds
    });
    if (error) {
      toast.error(t('toast.errorDeleteParticipants'));
    } else {
      const updatedClassroom = { ...data, 'user-ids': updatedUserIds };
      setClassroom(updatedClassroom);
      getAllUsers();
      toast.success(t('toast.successRemoveParticipant'));
    }
  };

  const moreThanThirtyDays = date => {
    const lastAccess = moment(date);
    const thirtyDaysAgo = moment().subtract(30, 'days');

    if (!date || lastAccess.isBefore(thirtyDaysAgo)) {
      return <span className="badge badge--tiny badge--warning">{t('journey.isInactive')}</span>;
    } else {
      return <span className="badge badge--tiny badge--success">{t('journey.isActive')}</span>;
    }
  };

  const filterUsers = users => {
    let filteredUsers = users;

    if (searchTerm !== '') {
      filteredUsers = filteredUsers.filter(a => a.name != null && a.name.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
    }

    return filteredUsers;
  };

  const registrationMethod = form => {
    switch (form) {
      case 'manual':
        return 'Manual';
      case 'invite':
        return 'Convite';
      case 'spreadsheet':
        return 'Importação por planilha';
    }
  };

  useEffect(() => {
    getClassroom();
    getAllUsers();
  }, []);

  useEffect(() => {
    if (allUsers) {
      const newFilteredUsers = filterUsers(allUsers);
      setFilteredUsers(newFilteredUsers);
    }
  }, [searchTerm, allUsers]);

  useEffect(() => {
    if (filteredUsers) {
      setClassroomManagers(filteredUsers?.filter(item => item.profiles?.some(profile => profile.name !== 'Aluno')));
      setClassroomStudents(filteredUsers?.filter(student => student.profiles?.some(profile => profile.name === 'Aluno')));
    }
  }, [filteredUsers]);

  const breadcrumbs = {
    title: 'Organização da Plataforma',
    nav: [
      {
        route: '/turmas',
        name: 'Turmas',
        isActive: false
      },
      {
        route: uri,
        name: classroom?.title,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="filter-bar">
        <div className="filter-bar__inner">
          <div className="filter-bar__row">
            <label
              htmlFor="search"
              className="filter-bar__label"
            >
              {t('textsCommon.research')}
            </label>
            <input
              className="form__control form__control--search-with-icon"
              placeholder={t('filter.participant')}
              type="search"
              name="search"
              id="search"
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {usePermission('Gerenciar turmas', 'Turmas') && (
          <div className="filter-bar__actions">
            <button
              className="btn btn--wide btn--primary"
              onClick={() => setShowModal(true)}
            >
              {t('courses.addParticpants')}
            </button>
          </div>
        )}
      </div>

      {loading && <Loader />}

      {!loading && !allUsers.length > 0 && (
        <EmptyState
          text={
            <span>
              <strong>Adicione</strong> à turma participantes que estão na plataforma.
            </span>
          }
        />
      )}

      {allUsers.length > 0 && (
        <div className="animate-fade-in">
          <section className="archive-section">
            <h3 className="archive-section__title mb-0">
              {classroomManagers?.length} {t('courses.managers')}
            </h3>

            <div className="round-dg-wrapper">
              <table
                className="round-dg round-dg--light"
                style={{ borderSpacing: '0 10px', minWidth: '800px' }}
              >
                <tbody className="round-dg__body">
                  {classroomManagers.map(user => {
                    return (
                      <tr
                        key={user.id}
                        className="round-dg__row"
                      >
                        <th className="round-dg__cell-header round-dg__cell-header--row">
                          <div className="round-dg__user">
                            <UserInfo user={user} />
                          </div>
                        </th>
                        <td className="round-dg__cell round-dg__cell--wrap">{user.email}</td>
                        <td className="round-dg__cell">
                          <td className="round-dg__cell">{moreThanThirtyDays(user['last-access'])}</td>
                        </td>
                        <td
                          className="round-dg__cell"
                          style={{ width: '66px' }}
                        >
                          {usePermission('Gerenciar turmas', 'Turmas') && (
                            <Dropdown>
                              <Dropdown.Toggle>
                                <FiMoreHorizontal />
                              </Dropdown.Toggle>

                              <Dropdown.Content>
                                <Dropdown.Item
                                  onClick={() => {
                                    Swal.fire({
                                      title: t('projects.warningDeleteTeacher'),
                                      text: t('projects.warningDeleteCannotBeUndoneText'),
                                      confirmButtonText: t('button.yes'),
                                      showDenyButton: true,
                                      denyButtonText: t('button.no'),
                                      showCloseButton: true
                                    }).then(res => {
                                      if (res.isConfirmed) {
                                        deleteParticipantFromClassroom(user.id);
                                      }
                                    });
                                  }}
                                >
                                  {t('button.remove')}
                                </Dropdown.Item>
                              </Dropdown.Content>
                            </Dropdown>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="archive-section">
            <h3 className="archive-section__title mb-0">
              {classroomStudents?.length} {t('courses.students')}
            </h3>

            <div className="round-dg-wrapper">
              <table
                className="round-dg round-dg--light"
                style={{ borderSpacing: '0 10px', minWidth: '800px' }}
              >
                <thead className="round-dg__header">
                  <tr className="round-dg__row">
                    <th className="round-dg__cell-header">{t('courses.thStudent')}</th>
                    <th className="round-dg__cell-header">E-mail</th>
                    <th className="round-dg__cell-header">{t('courses.thRegistrationMethod')}</th>
                    <th className="round-dg__cell-header" />
                    <th
                      className="round-dg__cell-header"
                      style={{ width: '66px' }}
                    />
                  </tr>
                </thead>

                <tbody className="round-dg__body">
                  {classroomStudents?.map((user, i) => {
                    // const updatedAt = moment(user['updated-at']);
                    return (
                      <tr
                        key={user.id}
                        className="round-dg__row"
                      >
                        <th className="round-dg__cell-header round-dg__cell-header--row">
                          <div className="round-dg__user">
                            <UserInfo user={user} />
                          </div>
                        </th>
                        <td className="round-dg__cell round-dg__cell--wrap">{user.email}</td>
                        {/* <td className="round-dg__cell">Último Acesso: {updatedAt.format('DD/MM/YYYY HH:mm')}</td> */}
                        <td className="round-dg__cell">{registrationMethod(user['registration-form'])}</td>
                        <td className="round-dg__cell">{moreThanThirtyDays(user['last-access'])}</td>

                        {/* <td className="round-dg__cell">
                        Participante desde 00/00/0000
                      </td> */}

                        <td className="round-dg__cell">
                          {usePermission('Gerenciar turmas', 'Turmas') && (
                            <Dropdown>
                              <Dropdown.Toggle>
                                <FiMoreHorizontal />
                              </Dropdown.Toggle>

                              <Dropdown.Content>
                                <Dropdown.Item
                                  onClick={() => {
                                    Swal.fire({
                                      title: t('projects.warningDeleteStudent'),
                                      text: t('projects.warningDeleteCannotBeUndoneText'),
                                      confirmButtonText: t('button.yes'),
                                      showDenyButton: true,
                                      denyButtonText: t('button.no'),
                                      showCloseButton: true
                                    }).then(res => {
                                      if (res.isConfirmed) {
                                        deleteParticipantFromClassroom(user.id);
                                      }
                                    });
                                  }}
                                >
                                  {t('button.remove')}
                                </Dropdown.Item>
                              </Dropdown.Content>
                            </Dropdown>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
      >
        <ParticipantsForm
          setShowModal={setShowModal}
          classroom={classroom}
          allUsers={allUsers}
          addParticipantToClassroom={addParticipantToClassroom}
          deleteParticipantFromClassroom={deleteParticipantFromClassroom}
        />
      </Modal>
    </DashboardContent>
  );
}
